import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const faqs = [
  {
    question: 'What industries do you serve?',
    answer: 'We specialize in technical support and help desk operations for electronics, robotics, industrial equipment, IT services (MSPs), and similar B2B companies with high inbound call volumes. If your team handles a large number of routine support calls, we can help.',
  },
  {
    question: 'What if the AI can\'t answer a question?',
    answer: 'It escalates to your human team immediately — with the full conversation transcript, caller information, and the AI\'s assessment of the issue. Your agent picks up with full context instead of starting from scratch. No caller is left without a resolution path.',
  },
  {
    question: 'What does integration look like?',
    answer: 'We connect to your existing CRM or helpdesk (Salesforce, Zendesk, ServiceNow, Freshdesk, HubSpot, Jira Service Management). When a call comes in, the AI handles it and automatically logs the interaction, creates tickets, and updates contact records. No manual data entry for your team.',
  },
  {
    question: 'Can it handle technical questions specific to our products?',
    answer: 'Yes. During setup, we train the AI on your knowledge base, product documentation, FAQ library, and common troubleshooting flows. It learns YOUR products and your specific terminology — not generic responses.',
  },
  {
    question: 'What happens during the 14-day pilot?',
    answer: 'We set up your AI agent, integrate it with your systems, and route real calls to it. You monitor every call via recordings and transcripts in a live dashboard. After 14 days, you decide if it\'s working. No payment until you say yes.',
  },
  {
    question: 'How is this different from a chatbot or IVR?',
    answer: 'This is a voice AI that answers phone calls — real conversations, not text bubbles or "press 1 for sales." It speaks naturally, understands context, asks follow-up questions, and takes actions (creates tickets, routes calls, resolves issues). 67% of callers hang up on IVR systems. Our AI has a <5% abandonment rate.',
  },
  {
    question: 'How long does setup take?',
    answer: '48–72 hours from signed agreement to live. You approve the final agent configuration before any calls are routed. Most teams are fully integrated within 3 business days.',
  },
  {
    question: 'Do I need to change my phone system?',
    answer: `Not necessarily. There are two ways to deploy:\n\nOption A — AI as your primary number (recommended): Your AI agent gets a dedicated phone number. You publish this as your main support line. Every call hits the AI first. When the AI can't resolve an issue — or the caller asks for a human — it auto-forwards directly to your existing team's number. This is the simplest setup and gives you maximum coverage. Your current phone system stays exactly as it is — it just receives forwarded calls from the AI instead of raw inbound volume.\n\nOption B — Conditional forwarding from your existing number: You keep your current support number and set up call forwarding through your phone provider or PBX to route specific traffic to the AI number. Common setups: after-hours calls forward to AI, overflow when hold times exceed a threshold, or specific departments route to AI. This configuration is managed by your IT team or phone provider — we'll advise on best practices but the forwarding setup is on your side.\n\nEither way, once the call reaches the AI, everything works the same. We handle all the AI configuration, integration, and optimization.`,
  },
  {
    question: 'How many calls can it handle simultaneously?',
    answer: 'Multiple calls at once — the exact number scales with your plan tier and grows as your volume does. During traffic spikes, burst capacity kicks in to handle overflow. No caller gets a busy signal. If your volume requires higher concurrency, we configure your plan accordingly.',
  },
  {
    question: 'What are the honest limitations?',
    answer: 'The AI answers questions and creates tickets — it does not log into or take actions inside your systems on behalf of callers. It cannot handle issues that require screen sharing or remote access, calls requiring legal or compliance judgment, or deeply emotional situations. These are escalated to your team immediately with full context. What it does handle conversationally: status inquiries, warranty questions, firmware guidance, troubleshooting walkthroughs, appointment scheduling, and FAQ resolution. We\'d rather be honest about this than oversell.',
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="py-12 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggle(i)}
                className="w-full px-6 py-4 text-left flex items-center justify-between bg-white hover:bg-slate-50 transition-colors"
              >
                <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                <SafeIcon icon={openIndex === i ? FiChevronUp : FiChevronDown} className="w-5 h-5 text-slate-500 flex-shrink-0" />
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}
                  >
                    <div className="px-6 pb-6 pt-2 text-slate-600 leading-relaxed bg-slate-50 border-t border-slate-100 whitespace-pre-line">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;